import {
  Archive,
  FileText,
  Inbox,
  Send,
  ShieldAlert,
  Star,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import type { JSX } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@mailflare/ui";
import type { Label, MailboxId, SystemFolder } from "@mailflare/shared";

import { routes } from "@/features/mailboxes/routes";

interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly to: string;
  readonly count?: number;
  readonly testId: string;
}

interface FolderItem {
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly folder: SystemFolder;
}

const FOLDER_ITEMS: readonly FolderItem[] = [
  { key: "sent", label: "Sent", icon: Send, folder: "sent" },
  { key: "drafts", label: "Drafts", icon: FileText, folder: "drafts" },
  { key: "archive", label: "Archive", icon: Archive, folder: "archive" },
  { key: "spam", label: "Spam", icon: ShieldAlert, folder: "spam" },
  { key: "trash", label: "Trash", icon: Trash2, folder: "trash" },
];

export interface SidebarProps {
  readonly mailboxId: MailboxId;
  readonly labels: readonly Label[];
  readonly unreadCount?: number;
  readonly onNavigate?: () => void;
}

function navClass(isActive: boolean): string {
  return cn(
    "flex items-center gap-4 rounded-md py-2 pr-4 pl-4 text-sm transition-colors",
    isActive
      ? "bg-accent text-accent-foreground font-medium"
      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground font-medium",
  );
}

export function Sidebar({ mailboxId, labels, unreadCount, onNavigate }: SidebarProps): JSX.Element {
  const inbox: NavItem = {
    key: "inbox",
    label: "Inbox",
    icon: Inbox,
    to: routes.folder(mailboxId, "inbox"),
    testId: "nav-inbox",
    ...(unreadCount !== undefined && unreadCount > 0 ? { count: unreadCount } : {}),
  };
  const items: readonly NavItem[] = [
    inbox,
    {
      key: "starred",
      label: "Starred",
      icon: Star,
      to: routes.starred(mailboxId),
      testId: "nav-starred",
    },
    ...FOLDER_ITEMS.map((item) => ({
      key: item.key,
      label: item.label,
      icon: item.icon,
      to: routes.folder(mailboxId, item.folder),
      testId: `nav-${item.key}`,
    })),
  ];

  return (
    <nav aria-label="Mailbox folders" className="flex h-full flex-col gap-0.5 overflow-y-auto p-3">
      <NavLink
        to={routes.inbox(mailboxId)}
        end
        onClick={onNavigate}
        className="text-foreground mb-4 flex items-center gap-3 px-3 py-1 text-xl tracking-tight"
      >
        <span className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-xl text-base font-bold">
          M
        </span>
        MailFlare
      </NavLink>

      {items.map((item) => (
        <NavLink
          key={item.key}
          to={item.to}
          onClick={onNavigate}
          data-testid={item.testId}
          className={({ isActive }) => navClass(isActive)}
        >
          <item.icon aria-hidden className="size-[18px] shrink-0" />
          <span className="flex-1">{item.label}</span>
          {item.count !== undefined ? (
            <span className="text-xs font-semibold" aria-label={`${item.count} unread`}>
              {item.count}
            </span>
          ) : null}
        </NavLink>
      ))}

      <div className="bg-border/70 my-3 h-px" role="presentation" />

      <p className="text-muted-foreground px-4 pb-1 text-xs font-semibold tracking-wide uppercase">
        Labels
      </p>
      {labels.length === 0 ? (
        <p className="text-muted-foreground px-4 py-1 text-xs">No labels yet</p>
      ) : (
        labels.map((label) => (
          <NavLink
            key={label.id}
            to={routes.label(mailboxId, label.id)}
            onClick={onNavigate}
            data-testid={`nav-label-${label.id}`}
            className={({ isActive }) => navClass(isActive)}
          >
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: label.color }}
            />
            <span className="flex-1 truncate">{label.name}</span>
          </NavLink>
        ))
      )}
    </nav>
  );
}
